<script>
	import { onMount, setContext } from 'svelte';
	import { mapMultiSelectMode, sessionExpired, mapInUse } from './stores/stores';
	import { handleKeyDown } from './scripts/keyboard';
	import Sidebar from './sidebar/Sidebar.svelte';
	import Map from './Map.svelte';
	import Login from './Login.svelte';
	import Toolbar from './Toolbar.svelte';
	import ApiClient, { SessionExpiredError } from './scripts/apiclient';
	import SidebarControls from './sidebar/SidebarControls.svelte';

	/**
	 * How long a session check is considered fresh. Keeps a bit of window switching back and
	 * forth from asking the server about the session over and over.
	 */
	const sessionCheckInterval = 30 * 1000;

	let loading = true;
	let error = null;
	let user = null;
	let map = null;

	/** A message to show above the login form, explaining why it is being shown. */
	let loginMessage = null;

	/** When the session was last checked, as a timestamp. */
	let sessionCheckedAt = 0;

	let sidebarInterface;

	$: mapStartingPosition = user && user.preferences ? user.preferences.startingPosition : null;

	// The api client flags the session as expired as soon as a request comes back
	// unauthorized. Showing the login form right away beats leaving an interface up where
	// every action fails.
	$: if ($sessionExpired) {
		user = null;
		loginMessage = 'Din session har gått ut. Logga in igen.';
	}

	function login({ username, password }) {
		return ApiClient.login(username, password)
			.then((res) => {
				if (!res.ok) {
					throw Error('Inloggningen misslyckades');
				}

				return res.json();
			})
			.then((u) => {
				sessionExpired.set(false);
				loginMessage = null;
				sessionCheckedAt = Date.now();
				return (user = u);
			});
	}

	function logout() {
		ApiClient.logout().then(() => {
			sessionExpired.set(false);
			loginMessage = null;
			user = null;
		});
	}

	/**
	 * Checks that the session is still alive, and shows the login form if it isn't. A failed
	 * check is ignored, so that a hiccup on the way to the server doesn't throw the user out.
	 */
	async function checkSession() {
		if (!user || Date.now() - sessionCheckedAt < sessionCheckInterval) {
			return;
		}

		sessionCheckedAt = Date.now();

		try {
			let sessionUser = await ApiClient.getSessionUser();
			if (!sessionUser) {
				sessionExpired.set(true);
			}
		} catch (err) {
			console.log('Could not check the session', err);
		}
	}

	function addPlayground() {
		map.addPlayground();
	}

	/**
	 * Scrolls the map to the specified position.
	 *
	 * @param pos {object} Location object with lat and lon parameters.
	 *
	 */
	function setPosition(pos) {
		map.scrollMap(pos);
	}

	function goToStartingPosition() {
		if (!user) {
			return;
		}

		let preferences = user.preferences || {};
		let pos = preferences.startingPosition;

		if (!pos) {
			return alert('Ingen startposition inställd');
		}

		map.scrollMapToStartingPosition(pos);
	}

	async function setUserStartingPosition() {
		if (!user) {
			return;
		}
		let position = map.getPosition();
		if (!position) {
			return;
		}

		let preferences = user.preferences || {};
		preferences.startingPosition = position;

		try {
			user = await ApiClient.updateUser(user, {
				preferences: preferences,
			});
		} catch (err) {
			console.log(err);

			// An expired session shows the login form, and asking the user to try again
			// there would only be misleading.
			if (!(err instanceof SessionExpiredError)) {
				alert('Startpositionen kunde inte sparas. Försök igen.');
			}
			return;
		}

		alert('Kartans startposition har sparats.');
	}

	// Check session status
	onMount(async () => {
		try {
			let sessionUser = await ApiClient.getSessionUser();
			sessionCheckedAt = Date.now();
			user = sessionUser;
			loading = false;
		} catch (err) {
			loading = false;
			console.log(err);
			error = `${err}`;
		}
	});

	setContext('getMap', () => map);
</script>

<!--
	The keydown handler is captured, so that the shortcuts of the app are handled before
	Leaflet sees the event. Sessions expire on their own, so the session is checked whenever
	the window is brought back up: a tab left open over the weekend then shows the login form
	instead of an interface where the first action fails.
-->
<svelte:window on:keydown|capture={handleKeyDown} on:focus={checkSession} />

{#if loading}
	<div class="m-2">
		<div class="alert alert-info" role="alert">
			<div class="spinner-border spinner-border-sm text-secondary m-2" role="status">
				<span class="sr-only" />
			</div>
			Laddar ...
		</div>
	</div>
{:else if error}
	<div class="m-2">
		<div class="alert alert-danger" role="alert">
			Servern verkar vara nere 😥 {error}
		</div>
	</div>
{:else if user}
	<div class="app" class:map-in-use={$mapInUse}>
		<div class="toolbar">
			<Toolbar setStartingPosition={setUserStartingPosition} {setPosition} {goToStartingPosition} {logout} {user} {addPlayground} />
		</div>
		<div class="controls">
			<SidebarControls {sidebarInterface} />
		</div>
		<div class="sidebar">
			<Sidebar bind:sidebarInterface />
		</div>
		<div class="map">
			<Map bind:this={map} startingPosition={mapStartingPosition} />
		</div>
	</div>
{:else}
	<Login submit={login} message={loginMessage} />
{/if}

<style>
	/* One grid rather than four boxes positioned against each other by hand. The height of
	   the toolbar, the height of the controls above the sidebar and the width of the sidebar
	   used to be written out in three files, where the map had to know where the sidebar
	   ended and the sidebar had to know how tall the toolbar and the controls were together.
	   They are properties of the shell now, and the breakpoint below only has to change one
	   of them. */
	.app {
		--toolbar-height: 50px;
		--controls-height: 40px;
		--sidebar-width: 300px;

		display: grid;
		height: 100%;
		grid-template-columns: var(--sidebar-width) 1fr;
		/* The toolbar sizes the row rather than the other way round, so it can grow by the
		   status bar inset without anything below it needing to know */
		grid-template-rows: auto var(--controls-height) 1fr;
		grid-template-areas:
			'toolbar toolbar'
			'controls map'
			'sidebar map';
	}

	.toolbar {
		grid-area: toolbar;
	}

	.controls {
		grid-area: controls;
	}

	.sidebar {
		grid-area: sidebar;
		overflow: auto;
		border-right: 1px solid #666;
		/* A grid item won't shrink below its content without this, and the list has to be
		   able to scroll rather than push the shell open */
		min-height: 0;
		/* Room at the end of the list for the home indicator */
		padding-bottom: env(safe-area-inset-bottom, 0px);
	}

	.map {
		grid-area: map;
		min-width: 0;
		min-height: 0;
		/* What the bar above the map measures its own edges against. This pane used to be
		   positioned, which gave it that job without anyone saying so, and laying the shell
		   out as a grid quietly took it away: the bar went back to the body and stretched
		   the width of the window, across the sidebar. */
		position: relative;
	}

	/* A phone shows one thing at a time. The sidebar has the screen to itself, and hands it
	   to the map for as long as something is being done there: a dialog, a playground being
	   put in place, playgrounds being picked out to merge. Whichever is not on show is taken
	   out rather than squeezed, since the 75px of map that was left beside a 300px sidebar
	   was too narrow to read, to pan or to hit a marker in, and below 300px of screen the
	   fixed column was wider than the window and the whole page slid sideways.

	   Above the breakpoint there is room for both and none of this applies. */
	@media (max-width: 991.98px) {
		.app {
			--sidebar-width: 100%;
		}

		.app .map {
			display: none;
		}

		/* The border of each pane would otherwise sit on the edge of the screen */
		.app .sidebar {
			border-right: none;
		}

		.app.map-in-use {
			--sidebar-width: 0px;
		}

		.app.map-in-use .sidebar,
		.app.map-in-use .controls {
			display: none;
		}

		.app.map-in-use .map {
			display: block;
		}
	}
</style>
